import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, useTheme, Dialog, DialogTitle, DialogContent, TextField, Button, MenuItem as MuiMenuItem, FormControlLabel, Radio, Avatar, Switch, Select, CircularProgress, Tooltip, Typography, Chip, Paper, List, ListItem, ListItemText, LinearProgress } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import Menu from "@mui/material/Menu";
import TuneIcon from "@mui/icons-material/Tune";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DeleteIcon from "@mui/icons-material/Delete";
import { updateModuleAccess, updateCanalAccess } from '../../store/preferencesSlice';
import { logout } from '../../services/auth';
import Badge from "@mui/material/Badge";
import { getNotifications, markNotificationRead, deleteNotification } from '../../services/favoriteService';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import { GREY_UNDEVELOPED_UI } from '../../services/config';
import ImportHistoryDialog from '../../components/ImportHistoryDialog';
import { useBrand } from '../../contexts/BrandContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { uploadDocument, listDocuments, deleteDocument } from '../../services/chatService';

// Helper component to render brand logo or avatar
const BrandLogoAvatar = ({ brand, size = 32 }) => {
  if (brand?.brand_logo_url) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: '#ffffff',
          padding: '4px'
        }}
      >
        <Box
          component="img"
          src={brand.brand_logo_url}
          alt={brand.brand_label || brand.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Avatar>
    );
  }

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: '#1976d2',
        fontSize: size * 0.4375,
        fontWeight: 600,
        color: '#fff'
      }}
    >
      {(brand?.name || 'B').charAt(0).toUpperCase()}
    </Avatar>
  );
};

const Topbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [brandAnchorEl, setBrandAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [lastNotifId, setLastNotifId] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [knowledgeBaseOpen, setKnowledgeBaseOpen] = useState(false);

  // Knowledge Base state
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const modulePreferences = useSelector(state => state.preferences.modules);
  const canalPreferences = useSelector(state => state.preferences.canaux);

  // Brand context for multi-brand support
  const { currentBrand, availableBrands, switching, switchBrand: handleBrandSwitch, hasMultipleBrands, isAuthenticated, loading: brandsLoading } = useBrand();

  // Subscription context for plan information
  const { subscription, refreshSubscription } = useSubscription();

  // Debug logging for brand state
  useEffect(() => {
    console.log('[Topbar] Brand State:', {
      isAuthenticated,
      brandsLoading,
      currentBrand: currentBrand?.name,
      availableBrandsCount: availableBrands?.length,
      hasMultipleBrands,
      availableBrands: availableBrands?.map(b => ({ id: b.id, name: b.name }))
    });
  }, [currentBrand, availableBrands, hasMultipleBrands, isAuthenticated, brandsLoading]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load Knowledge Base documents when dialog opens
  useEffect(() => {
    const fetchDocuments = async () => {
      if (knowledgeBaseOpen) {
        setIsLoadingDocuments(true);
        try {
          const docs = await listDocuments();
          setDocuments(docs);
        } catch (error) {
          console.error('Failed to fetch documents:', error);
        } finally {
          setIsLoadingDocuments(false);
        }
      }
    };

    fetchDocuments();
  }, [knowledgeBaseOpen]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const notifs = await getNotifications();
      const latest = notifs.find(n => !n.is_read);
      if (latest && latest.id !== lastNotifId) {
        setSnackbarMsg(latest.message);
        setSnackbarOpen(true);
        setLastNotifId(latest.id);
      }
      setNotifications(notifs);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleUserClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Ensure the latest subscription is fetched when opening the menu
    if (refreshSubscription) {
      refreshSubscription();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
    navigate("/login");
  };

  const handleModuleToggle = (module) => {
    dispatch(updateModuleAccess({ module, value: !modulePreferences[module] }));
  };

  const handleCanalToggle = (canal) => {
    dispatch(updateCanalAccess({ canal, value: !canalPreferences[canal] }));
  };

  const handleProfilePhotoChange = (event) => {
    setProfilePhotoPreview(URL.createObjectURL(event.target.files[0]));
  };

  const handleRemoveProfilePhoto = () => {
    setProfilePhotoPreview(null);
  };

  const handleSavePreferences = () => {
    // La sauvegarde est déjà gérée par Redux
    setPreferencesOpen(false);
  };

  const handleNotifClick = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };
  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };
  const handleMarkRead = async (notifId) => {
    await markNotificationRead(notifId);
    fetchNotifications();
  };
  const handleDeleteNotif = async (notifId) => {
    await deleteNotification(notifId);
    fetchNotifications();
  };

  // Knowledge Base handlers
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 100);

      // Upload the document
      await uploadDocument(selectedFile);

      // Clear the interval and set progress to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Refresh the document list
      const docs = await listDocuments();
      setDocuments(docs);

      // Reset the file input
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Show success message
      setSnackbarMsg('Document uploaded successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to upload document:', error);
      setSnackbarMsg(`Failed to upload document: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteDocument(documentId);

        // Refresh the document list
        const docs = await listDocuments();
        setDocuments(docs);

        // Show success message
        setSnackbarMsg('Document deleted successfully!');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Failed to delete document:', error);
        setSnackbarMsg(`Failed to delete document: ${error.message}`);
        setSnackbarOpen(true);
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={2}
      sx={{
        background: '#fff',
        borderBottom: '1px solid #e9ecef',
        minHeight: 64,
        boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* SEARCH BAR */}
      <Box
        display="flex"
        alignItems="center"
        backgroundColor="#f1f3f4"
        borderRadius="8px"
        border="1px solid #e9ecef"
        boxShadow="none"
        sx={{ minWidth: 220, height: 38, mr: 2 }}
      >
        <InputBase
          sx={{ ml: 2, flex: 1, color: '#495057', fontWeight: 500, fontSize: 15, fontFamily: 'Montserrat, Arial, sans-serif' }}
          placeholder="Search"
          inputProps={{ style: { color: '#495057' } }}
        />
        <IconButton type="button" sx={{ p: 1, color: '#495057' }}>
          <SearchIcon />
        </IconButton>
      </Box>

      {/* Spacer to push icons to the right */}
      <Box sx={{ flexGrow: 1 }} />

      {/* ICONS */}
      <Box display="flex" alignItems="center" gap={1}>
        {/* BRAND SELECTOR - Circular Avatar Style */}
        {isAuthenticated && (brandsLoading || availableBrands?.length > 0 || currentBrand) && (
          <Box>
            {brandsLoading ? (
              // Show loading state
              <IconButton disabled sx={{ bgcolor: '#f1f3f4' }}>
                <CircularProgress size={24} />
              </IconButton>
            ) : hasMultipleBrands ? (
              // Show clickable brand avatar with menu
              <Box>
                <Tooltip title={currentBrand?.brand_label || currentBrand?.name || 'Select Brand'} arrow>
                  <IconButton
                    onClick={(e) => setBrandAnchorEl(e.currentTarget)}
                    disabled={switching}
                    sx={{
                      bgcolor: '#f1f3f4',
                      '&:hover': { bgcolor: '#e9ecef' },
                      width: 40,
                      height: 40
                    }}
                  >
                    {switching ? (
                      <CircularProgress size={24} />
                    ) : (
                      <BrandLogoAvatar brand={currentBrand} size={32} />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={brandAnchorEl}
                  open={Boolean(brandAnchorEl)}
                  onClose={() => setBrandAnchorEl(null)}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      borderRadius: 2
                    }
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef' }}>
                    <Box sx={{ fontSize: 12, color: '#868e96', fontWeight: 500 }}>SELECT BRAND</Box>
                  </Box>
                  {availableBrands.map((brand) => (
                    <MenuItem
                      key={brand.id}
                      onClick={() => {
                        handleBrandSwitch(brand.id);
                        setBrandAnchorEl(null);
                      }}
                      selected={brand.id === currentBrand?.id}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          bgcolor: '#e3f2fd',
                          '&:hover': {
                            bgcolor: '#bbdefb'
                          }
                        }
                      }}
                    >
                      <Box sx={{ mr: 1.5 }}>
                        <BrandLogoAvatar brand={brand} size={32} />
                      </Box>
                      <Box>
                        <Box sx={{ fontWeight: 500, fontSize: 14 }}>
                          {brand.brand_label || brand.name}
                        </Box>
                        {brand.brand_label && brand.brand_label !== brand.name && (
                          <Box sx={{ fontSize: 11, color: '#868e96' }}>{brand.name}</Box>
                        )}
                        {brand.id === currentBrand?.id && (
                          <Box sx={{ fontSize: 11, color: '#1976d2', fontWeight: 500 }}>Current</Box>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : currentBrand ? (
              // Show current brand avatar (non-interactive for single brand)
              <Tooltip title={currentBrand.brand_label || currentBrand.name} arrow>
                <IconButton
                  disabled
                  sx={{
                    bgcolor: '#f1f3f4',
                    width: 40,
                    height: 40,
                    cursor: 'default',
                    '&.Mui-disabled': {
                      bgcolor: '#f1f3f4'
                    }
                  }}
                >
                  <BrandLogoAvatar brand={currentBrand} size={32} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        )}

        <IconButton onClick={handleNotifClick} sx={{ color: '#495057', bgcolor: '#f1f3f4', '&:hover': { bgcolor: '#e9ecef' } }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
        <Menu anchorEl={notifAnchorEl} open={Boolean(notifAnchorEl)} onClose={handleNotifClose}>
          {notifLoading ? (
            <MenuItem disabled>Chargement...</MenuItem>
          ) : notifications.length === 0 ? (
            <MenuItem disabled>Aucune notification</MenuItem>
          ) : notifications.map(notif => (
            <MenuItem key={notif.id} sx={{ whiteSpace: 'normal', minWidth: 300, bgcolor: notif.is_read ? undefined : '#e3f0fe' }}>
              <Box flex={1} onClick={() => handleMarkRead(notif.id)} style={{ cursor: 'pointer' }}>
                {notif.message}
                <Box fontSize={12} color="#868e96">{new Date(notif.created_at).toLocaleString()}</Box>
              </Box>
              <IconButton size="small" onClick={() => handleDeleteNotif(notif.id)}><span style={{ fontSize: 16 }}>🗑️</span></IconButton>
            </MenuItem>
          ))}
        </Menu>
        <IconButton onClick={handleUserClick} sx={{ color: '#495057', bgcolor: '#f1f3f4', '&:hover': { bgcolor: '#e9ecef' } }}>
          <PersonOutlinedIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {subscription && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e9ecef' }}>
              <Typography variant="caption" sx={{ color: '#868e96', fontSize: 11, fontWeight: 500 }}>
                PLAN ACTUEL
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Chip
                  label={subscription}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 12,
                    fontWeight: 700,
                    background: subscription === 'Demo' ? 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)' :
                      subscription === 'Growth' ? 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)' :
                        subscription === 'Pro' ? 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)' :
                          'linear-gradient(135deg, #ffa726 0%, #f57c00 100%)',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          )}
          <MenuItem onClick={() => { handleClose(); navigate('/preferences/subscription'); }} disabled={GREY_UNDEVELOPED_UI}>
            <LoyaltyIcon fontSize="small" sx={{ mr: 1, color: GREY_UNDEVELOPED_UI ? '#9e9e9e' : '#1976d2' }} />
            Mon Abonnement
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); navigate('/preferences/billing'); }} disabled={GREY_UNDEVELOPED_UI}>
            <ReceiptLongIcon fontSize="small" sx={{ mr: 1, color: GREY_UNDEVELOPED_UI ? '#9e9e9e' : '#10B981' }} />
            Facturation
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setPreferencesOpen(true); }} disabled={GREY_UNDEVELOPED_UI}>
            <TuneIcon fontSize="small" sx={{ mr: 1, color: '#9e9e9e' }} />
            Préférences utilisateur
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setProfileOpen(true); }} disabled={GREY_UNDEVELOPED_UI}>
            <ManageAccountsIcon fontSize="small" sx={{ mr: 1, color: '#9e9e9e' }} />
            Gestion du profile
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setHistoryDialogOpen(true); }}>
            <HistoryIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
            Historique d'import
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setKnowledgeBaseOpen(true); }} disabled={GREY_UNDEVELOPED_UI}>
            <MenuBookIcon fontSize="small" sx={{ mr: 1, color: GREY_UNDEVELOPED_UI ? '#9e9e9e' : '#1976d2' }} />
            Knowledge Base
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon fontSize="small" sx={{ mr: 1, color: '#e57373' }} />
            Déconnexion
          </MenuItem>
        </Menu>

        {/* Dialog Préférences */}
        <Dialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 20, textAlign: 'center', fontFamily: 'Montserrat, Arial, sans-serif' }}>
            Préférences Utilisateur
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 380, p: 1 }}>
              {/* Modules */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Modules</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={modulePreferences.cdp}
                        onChange={() => handleModuleToggle('cdp')}
                        color="primary"
                      />
                    }
                    label="CDP"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={modulePreferences.marketing}
                        onChange={() => handleModuleToggle('marketing')}
                        color="primary"
                      />
                    }
                    label="Marketing"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={modulePreferences.fidelite}
                        onChange={() => handleModuleToggle('fidelite')}
                        color="primary"
                      />
                    }
                    label="Fidélité"
                  />
                </Box>
              </Box>

              {/* Canaux */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Canaux</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={canalPreferences.sms}
                        onChange={() => handleCanalToggle('sms')}
                        color="primary"
                      />
                    }
                    label="SMS"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={canalPreferences.email}
                        onChange={() => handleCanalToggle('email')}
                        color="primary"
                      />
                    }
                    label="Email"
                  />
                </Box>
              </Box>

              {/* Interface */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Interface</Box>
                <TextField
                  label="Langue de l'interface :"
                  select
                  fullWidth
                  size="small"
                  defaultValue="fr"
                  sx={{ mb: 2, maxWidth: 260 }}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">Anglais</MenuItem>
                </TextField>
                <Box sx={{ fontWeight: 500, fontSize: 15, mb: 1 }}>Thème d'affichage :</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2, mb: 2 }}>
                  <FormControlLabel value="light" control={<Radio checked />} label="Clair" />
                  <FormControlLabel value="dark" control={<Radio />} label="Sombre" />
                  <FormControlLabel value="system" control={<Radio />} label="Thème du système" />
                  <Box sx={{ color: '#868e96', fontSize: 13, mt: 1 }}>
                    Utilisera le thème clair ou sombre de votre navigateur/OS.
                  </Box>
                </Box>
              </Box>

              {/* Notifications */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Notifications</Box>
                <Box sx={{ fontWeight: 500, fontSize: 15, mb: 0.5 }}>Recevoir les insights proactifs par :</Box>
                <Box sx={{ color: '#868e96', fontSize: 13, mb: 1 }}>
                  Comment souhaitez-vous être informé des nouvelles opportunités détectées par l'IA ?
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2 }}>
                  <FormControlLabel value="app" control={<Radio checked />} label="Notification dans l'application (ex: badge, zone dédiée)" />
                  <FormControlLabel value="email" control={<Radio />} label="E-mail (si fonctionnalité activée)" />
                </Box>
              </Box>

              {/* Comportement du Co-Pilote IA */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Comportement du Co-Pilote IA</Box>
                <Box sx={{ fontWeight: 500, fontSize: 15, mb: 0.5 }}>Niveau de détail des réponses par défaut :</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2, mb: 2 }}>
                  <FormControlLabel value="concise" control={<Radio checked />} label="Concise (résumé direct)" />
                  <FormControlLabel value="standard" control={<Radio />} label="Standard (avec un peu de contexte)" />
                  <FormControlLabel value="detailed" control={<Radio />} label="Détaillée (avec explications complètes)" />
                  <Box sx={{ color: '#868e96', fontSize: 13, mt: 1 }}>
                    Vous pourrez toujours demander plus ou moins de détails dans le chat.
                  </Box>
                </Box>
                <Box sx={{ fontWeight: 500, fontSize: 15, mb: 0.5 }}>Affichage des sources :</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e3e7ed', borderRadius: 2, p: 2 }}>
                  <FormControlLabel value="always" control={<Radio checked />} label="Toujours afficher" />
                  <FormControlLabel value="on-demand" control={<Radio />} label="Afficher seulement sur demande" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2, fontWeight: 600, fontSize: 15, px: 3, py: 1, background: '#1976d2' }}
                  onClick={handleSavePreferences}
                >
                  Enregistrer les préférences
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Dialog Gestion du profil */}
        <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 20, textAlign: 'center', fontFamily: 'Montserrat, Arial, sans-serif' }}>
            Gestion de votre compte
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 380, p: 1 }}>
              {/* Informations du Profil */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Informations du Profil</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ width: 64, height: 64, mb: 1 }} src={profilePhotoPreview || undefined} />
                    <Button component="label" variant="outlined" size="small" sx={{ fontSize: 13, py: 0.5, px: 1.5 }}>
                      {profilePhotoPreview ? 'Changer la photo' : 'Ajouter une photo'}
                      <input type="file" accept="image/*" hidden onChange={handleProfilePhotoChange} />
                    </Button>
                    {profilePhotoPreview && (
                      <Button color="error" size="small" sx={{ mt: 0.5, fontSize: 12 }} onClick={handleRemoveProfilePhoto}>
                        Supprimer
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField label="Nom complet :" fullWidth size="small" defaultValue="[Nom Utilisateur Actuel]" sx={{ mb: 2, maxWidth: 340 }} />
                    <TextField label="Adresse E-mail :" fullWidth size="small" defaultValue="[email@utilisateur.com]" sx={{ mb: 1, maxWidth: 340 }} InputProps={{ readOnly: true }} />
                    <Box sx={{ color: '#868e96', fontSize: 13, mb: 2, ml: 1 }}>
                      L'adresse e-mail ne peut pas être modifiée ici. Contactez un administrateur si nécessaire.
                    </Box>
                    <TextField label="Titre / Fonction (Optionnelle) :" fullWidth size="small" defaultValue="[Titre Actuel]" sx={{ mb: 2, maxWidth: 340 }} />
                    <TextField
                      label="Rôle(s) attribué(s) :"
                      fullWidth
                      size="small"
                      defaultValue="[Rôle(s) de l'utilisateur, ex: Marketeur, Analyste]"
                      InputProps={{ readOnly: true }}
                      sx={{ mb: 2, maxWidth: 340 }}
                    />
                  </Box>
                </Box>
                <Button variant="contained" color="primary" sx={{ fontWeight: 600, fontSize: 15, px: 3, py: 1, background: '#1976d2' }}>
                  Enregistrer les modifications
                </Button>
              </Box>
              {/* Changer le mot de passe */}
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Changer le mot de passe</Box>
                <TextField label="Mot de passe actuel :" fullWidth size="small" type="password" sx={{ mb: 2, maxWidth: 340 }} />
                <TextField label="Nouveau mot de passe :" fullWidth size="small" type="password" sx={{ mb: 1, maxWidth: 340 }} />
                <Box sx={{ color: '#868e96', fontSize: 13, mb: 2, ml: 1 }}>
                  Conseillé : Au moins 8 caractères, incluant majuscules, minuscules, chiffres et symboles.
                </Box>
                <TextField label="Confirmer le nouveau mot de passe :" fullWidth size="small" type="password" sx={{ mb: 2, maxWidth: 340 }} />
                <Button variant="contained" color="primary" sx={{ fontWeight: 600, fontSize: 15, px: 3, py: 1, background: '#1976d2' }}>
                  Changer le mot de passe
                </Button>
              </Box>
              {/* Rôle et Informations */}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MuiAlert onClose={handleSnackbarClose} severity="info" sx={{ width: '100%' }}>
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>

      {/* Import History Dialog */}
      <ImportHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />

      {/* Knowledge Base Dialog */}
      <Dialog open={knowledgeBaseOpen} onClose={() => {
        setKnowledgeBaseOpen(false);
        setSelectedFile(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, textAlign: 'center', fontFamily: 'Montserrat, Arial, sans-serif' }}>
          Knowledge Base
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Upload Document */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, background: '#fff', boxShadow: '0 2px 12px 0 rgba(25, 118, 210, 0.06)' }}>
              <Typography variant="subtitle1" sx={{ color: '#1976d2', fontWeight: 700, mb: 1, fontFamily: 'Montserrat, Arial, sans-serif' }}>
                Upload Document
              </Typography>
              <Box mb={1}>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="document-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.docx,.txt,.md,.csv"
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={isUploading}
                  >
                    {selectedFile ? selectedFile.name : 'Select File'}
                  </Button>
                </label>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleUploadDocument}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload Document'}
                </Button>

                {isUploading && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Document List */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, background: '#fff', boxShadow: '0 2px 12px 0 rgba(25, 118, 210, 0.06)' }}>
              <Typography variant="subtitle1" sx={{ color: '#1976d2', fontWeight: 700, mb: 1, fontFamily: 'Montserrat, Arial, sans-serif' }}>
                Documents
              </Typography>

              {isLoadingDocuments ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" py={1}>
                  No documents uploaded yet
                </Typography>
              ) : (
                <List dense>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.document_id}
                      secondaryAction={
                        <Tooltip title="Supprimer">
                          <IconButton edge="end" onClick={() => handleDeleteDocument(doc.document_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemText
                        primary={doc.filename}
                        secondary={`Uploaded: ${new Date(doc.upload_date).toLocaleDateString()}`}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: 14,
                          noWrap: true
                        }}
                        secondaryTypographyProps={{
                          fontSize: 12
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Topbar;
